<template>
  <dx-accordion
    class="mb-2"
    :on-open="loadDetails"
    :is-init-open="initiallyOpen || !!offerDetails"
    allow-text-select
  >
    <template slot="headline">
      <div class="row headline">
        <div class="col-12 col-md-3">
          {{ offer.policyholder.fullName }}
        </div>
        <div class="col-12 col-md-3">
          {{ offer.number }}
        </div>
        <div class="col-12 col-md-2">
          {{ productName(offer) }}
        </div>
        <div class="col-12 col-md-3">
          {{ formatPrice(offer) }}
        </div>
        <div class="col-12 col-md-1">
          <div
            :class="offer.policyholder.hasEbill ? 'icon-check' : 'icon-cross'"
          />
        </div>
      </div>
    </template>

    <template slot="content">
      <dx-loading-overlay v-if="forceReload || (isLoading && !offerDetails)" />
      <div v-if="!forceReload && offerDetails">
        <div class="row">
          <div
            v-if="offerDetails.editable"
            class="col mt-4 text-right edit-icons"
          >
            <button
              class="mr-2 d-inline-block btn-blank icon icon-edit"
              @click="loadFullOfferAndEdit()"
            >
              <span class="visually-hidden">
                {{ $t('updateRecords') }}
              </span>
            </button>

            <button
              class="d-inline-block btn-blank icon icon-delete"
              @click="openRejectionOverlay"
            >
              <span class="visually-hidden">
                {{ $t('partnerPortal.offerReject') }}
              </span>
            </button>
          </div>

          <dx-popup-modal ref="rejectionOverlay">
            <h3>{{ $t('partnerPortal.offerReject') }}</h3>
            <p>
              {{ $t('partnerPortal.offerRejectModalText') }}
              <dx-select
                v-model="rejectionReason"
                v-validate.disable="'required'"
                :options="offerRejectOptions"
                class="mt-2 mb-4"
                :error="errors.first('rejectionReason')"
                data-vv-name="rejectionReason"
              />
            </p>
            <div
              class="d-flex flex-wrap-reverse justify-content-center d-sm-block text-right"
            >
              <span
                class="abort-btn mt-2 mt-sm-0 secondary-link btn-blank"
                @click="closeRejectionOverlay"
              >
                {{ $t('abort') }}
              </span>

              <dx-button
                type="button"
                class="d-inline ml-sm-3"
                :text="$t('partnerPortal.offerReject')"
                small
                @click="confirmOfferRejection"
              />
            </div>
          </dx-popup-modal>
        </div>

        <dx-popup-modal v-if="isScipSales" ref="changeTariff">
          <h3>{{ $t('offer.changeTariff') }}</h3>

          <div class="row">
            <div
              v-for="tariff in possibleTariffSelection"
              :key="tariff.tariffName"
              class="`col-12 change-tariff-modal-tariffs-entry"
              :class="{
                'col-lg-4': possibleTariffSelection.length > 2,
                'col-lg-6': possibleTariffSelection.length === 2,
                'col-lg-12': possibleTariffSelection.length === 1,
              }"
            >
              <h4>{{ tariff.tariffName }}</h4>
              <dx-button
                :small="true"
                :text="$t('offer.changeTariff')"
                @click="handleChangeTariff(tariff)"
              />
            </div>
          </div>
        </dx-popup-modal>

        <h5>{{ $t('partnerPortal.offerDetails') }}</h5>

        <div class="row">
          <div class="col-12 col-md-7">
            <ul class="neutral">
              <li>
                {{ $t('partnerPortal.offerCalc') }}:
                {{ getFormattedDate(offerDetails.calculatedAt) }}
              </li>
              <li>
                {{ $t('partnerPortal.plannedInsuranceStart') }}:
                {{ getFormattedDate(offerDetails.startOfInsurance) }}
              </li>
              <li>
                {{ $t('partnerPortal.product') }}:
                {{ productName(offerDetails) }}
              </li>
              <li
                v-if="
                  !offerDetails.hasOpenUnderwriting ||
                  offerDetails.documents.length
                "
                class="mt-4"
              >
                <dx-download-list
                  :items="offerDetails.documents"
                  :items-inline="true"
                  tag-type="h4"
                />
              </li>
            </ul>
          </div>
          <div class="col-12 col-md-5">
            {{ $t('partnerPortal.grossPremium') }}:
            {{ formatPrice(offerDetails) }}
          </div>
        </div>

        <div class="row flex-row-reverse mt-3">
          <div class="col">
            <div v-if="isScipSales" class="text-right mb-3">
              <button
                type="button"
                class="abort-btn secondary-link btn-blank"
                @click="openChangeTariffOverlay"
              >
                {{ $t('offer.changeTariff') }}
              </button>
            </div>
            <dx-button
              v-if="offerDetails.acceptable"
              :small="true"
              :text="$t('partnerPortal.acceptOffer')"
              class="text-right mb-3"
              @click="loadFullOfferAndEdit()"
            />
            <dx-button
              v-if="offerDetails.reactivatable"
              :small="true"
              :text="$t('partnerPortal.reactivateOffer')"
              class="text-right mb-3"
              @click="reactivate"
            />

            <div
              v-if="offerDetails.hasOpenUnderwriting"
              class="icon-underwriting mb-3"
            >
              {{ $t('partnerPortal.offerAtUnderwriting') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </dx-accordion>
</template>

<script>
import {
  DxButton,
  DxAccordion,
  DxSelect,
  DxPopupModal,
} from '@sumcumo/dextra-frontend-component-lib-old'
import { scrollIntoView } from '~/javascripts/helper/scollIntoView'
import DxDownloadList from '~/components/DX-DownloadList'
import DxLoadingOverlay from '~/components/DX-LoadingOverlay'
import formatOfferPriceMixin from '~/javascripts/mixins/formatOfferPriceMixin'
import dateMixin from '~/javascripts/mixins/dateMixin'

import tariffQuery from '~/domains/partnerPortal/__gql__/queries/tariffs.gql'

export default {
  name: 'OfferListItem',
  components: {
    DxAccordion,
    DxButton,
    DxDownloadList,
    DxLoadingOverlay,
    DxPopupModal,
    DxSelect,
  },
  mixins: [dateMixin, formatOfferPriceMixin],
  props: {
    offer: {
      type: Object,
      default() {
        return {}
      },
    },
    loadOffer: {
      type: Function,
      default: () => null,
    },
    rejectOffer: {
      type: Function,
      default: () => null,
    },
    reactivateOffer: {
      type: Function,
      default: () => null,
    },
    initiallyOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      offerDetails: null,
      rejectionReason: null,
      isLoading: false,
      forceReload: false,
      allPossibleTariffs: null,
    }
  },
  computed: {
    isScipSales() {
      return (
        this.offerDetails.productCode === 'ssc2020' &&
        process.client &&
        window.localStorage.getItem('@scip-sales/url')
      )
    },
    possibleTariffSelection() {
      if (!this.allPossibleTariffs) return []
      const currentTariff = this.allPossibleTariffs.find(
        (tariff) => tariff.tariffName === this.offer.productName,
      )

      if (!currentTariff) return []

      const isCurrentBusiness = currentTariff.slug.startsWith('business')

      return this.allPossibleTariffs.filter((tariff) => {
        const isBusiness = tariff.slug.startsWith('business')
        const isPrivate = tariff.slug.startsWith('private')

        const isSameCategory =
          isCurrentBusiness === true ? isBusiness === true : isPrivate === true

        const hasDifferentName = tariff.tariffName !== this.offer.productName

        return isSameCategory && hasDifferentName
      })
    },
    offerRejectOptions() {
      const defaultOption = {
        value: '',
        label: this.$t('selectOption'),
        caption: true,
      }

      const reasons = [
        'RESIGN',
        'PRODUCT_UNSUITABLE',
        'COMPETITOR_CHEAPER',
        'COMPETITOR_BETTER',
        'OFFER_WITHDRAWN',
      ].map((reason) => ({
        value: reason,
        label: this.$t(`partnerPortal.offerRejectionReason.${reason}`),
      }))

      return [defaultOption, ...reasons]
    },
  },
  mounted() {
    if (this.initiallyOpen) {
      scrollIntoView(this.$el)
    }
  },
  methods: {
    async handleChangeTariff(tariff) {
      const variantKey = this.getProductDetailsForTariff(tariff)
      this.$refs.changeTariff.close()
      const { productKey, productVersionNumber } = this.offerDetails

      // open offer in SCIP-Sales
      this.$router.push({
        path: this.localePath('BrokerOffer-Offer'),
        query: {
          contractId: this.offer.id,
          key: productKey,
          variantKey,
          versionNumber: productVersionNumber,
        },
      })
    },
    async loadTariffs() {
      const { data } = await this.$apolloProvider.clients.cms.query({
        query: tariffQuery,
        variables: {
          locales: [this.$i18n.locale],
        },
      })

      if (!data) return null

      return data.page.edges.map((item) => ({
        ...item.node,
        benefitHeadline: item.node.tariffBenefitList?.headline,
        benefits: item.node.tariffBenefitList?.listEntries,
        benefitCopy: item.node.tariffBenefitCopy,
        additionalItemsHeadline: item.node.tariffAdditionalList?.headline,
        additionalItems: item.node.tariffAdditionalList?.listEntries,
        tariffExtras: item.node.tariffExtrasList?.listEntries,
      }))
    },
    async loadFullOfferAndEdit() {
      this.isLoading = true
      try {
        const {
          productName,
          productCode,
          productKey,
          productVariantKey,
          productVersionNumber,
        } = this.offerDetails

        if (productCode === 'ssc2020') {
          // open offer in SCIP-Sales
          this.$router.push({
            path: this.localePath('BrokerOffer-Offer'),
            query: {
              contractId: this.offer.id,
              key: productKey,
              variantKey: productVariantKey,
              versionNumber: productVersionNumber,
            },
          })
        } else {
          const result = await this.$store.dispatch('EDIT_OFFER', {
            id: this.offer.id,
            tariff: productName,
          })
          if (result) {
            this.$router.push({
              path: this.localePath('BrokerOffer-Summary'),
              query: { mode: 'edit' },
            })
          }
        }
      } finally {
        this.isLoading = false
      }
    },
    async loadDetails() {
      this.isLoading = true
      try {
        const result = await this.loadOffer(this.offer.id)
        this.allPossibleTariffs = await this.loadTariffs()
        this.isLoading = false
        if (result) {
          this.offerDetails = result
        }
      } finally {
        this.isLoading = false
        this.forceReload = false
      }
    },
    openRejectionOverlay() {
      this.rejectionReason = null
      this.$refs.rejectionOverlay.open()
    },
    getVariantForTariff(tariff) {
      const isBusiness = tariff.slug.startsWith('business')

      let productVariant = isBusiness ? 'flex_business' : 'flex_dex'
      if (tariff.slug.includes('TariffXL')) {
        productVariant = isBusiness ? 'business_xl' : 'dex_xl'
      }
      if (tariff.slug.includes('TariffL')) {
        productVariant = isBusiness ? 'business_l' : 'dex_l'
      }

      return productVariant
    },
    openChangeTariffOverlay() {
      this.$refs.changeTariff.open()
    },
    closeRejectionOverlay() {
      this.$refs.rejectionOverlay.close()
    },
    async confirmOfferRejection() {
      const valid = await this.$validator.validate()
      if (valid) {
        this.rejectOffer(
          this.offer.id,
          this.rejectionReason,
          this.closeRejectionOverlay,
          this.fullReload,
        )
      }
    },
    fullReload() {
      this.forceReload = true
      this.loadDetails()
    },
    reactivate() {
      this.reactivateOffer(this.offer.id, this.fullReload)
    },
    productName(offer) {
      return offer.options || offer.productName
    },
  },
}
</script>

<style lang="scss" scoped>
@import './index';
</style>
